import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Calendar, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAppointments({ user, setUser }) {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchAppointments();
  }, [user, navigate]);

  const fetchAppointments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/appointments`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAppointments(data.appointments || []);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar citas');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/admin/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (response.ok) {
        toast.success('Estado actualizado');
        fetchAppointments();
      }
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    navigate('/');
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-green-100 text-green-800',
      completed: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100';
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center">Cargando...</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-black text-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display' }}>ADMIN - ARTHUR FITZ</h1>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white hover:underline">Ver Tienda</Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white">
              <LogOut className="w-4 h-4 mr-2" /> Cerrar Sesión
            </Button>
          </div>
        </div>
      </header>

      <nav className="bg-white border-b">
        <div className="container mx-auto px-6 py-3">
          <div className="flex gap-6">
            <Link to="/admin" className="text-gray-600 hover:text-black">Dashboard</Link>
            <Link to="/admin/products" className="text-gray-600 hover:text-black">Productos</Link>
            <Link to="/admin/orders" className="text-gray-600 hover:text-black">Pedidos</Link>
            <Link to="/admin/customers" className="text-gray-600 hover:text-black">Clientes</Link>
            <Link to="/admin/coupons" className="text-gray-600 hover:text-black">Cupones</Link>
            <Link to="/admin/quotes" className="text-gray-600 hover:text-black">Presupuestos</Link>
            <Link to="/admin/shipping" className="text-gray-600 hover:text-black">Envios</Link>
            <Link to="/admin/contact-requests" className="font-medium text-black border-b-2 border-black pb-2">Solicitudes</Link>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        <h2 className="text-3xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>Gestión de Citas Telefónicas</h2>

        <div className="bg-white border-2 border-black">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-black">
                  <th className="text-left p-4">Cliente</th>
                  <th className="text-left p-4">Contacto</th>
                  <th className="text-left p-4">Fecha</th>
                  <th className="text-left p-4">Hora</th>
                  <th className="text-left p-4">Mensaje</th>
                  <th className="text-left p-4">Estado</th>
                  <th className="text-left p-4">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {appointments.length > 0 ? (
                  appointments.map((appointment) => (
                    <tr key={appointment.id} className="border-b hover:bg-gray-50">
                      <td className="p-4">
                        <p className="font-medium">{appointment.name}</p>
                      </td>
                      <td className="p-4">
                        <p className="text-sm">{appointment.email}</p>
                        <p className="text-sm">{appointment.phone}</p>
                      </td>
                      <td className="p-4">{appointment.date}</td>
                      <td className="p-4">{appointment.time}</td>
                      <td className="p-4">
                        <p className="text-sm max-w-xs truncate">{appointment.message || '-'}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded text-xs ${getStatusColor(appointment.status)}`}>
                          {appointment.status}
                        </span>
                      </td>
                      <td className="p-4">
                        <select
                          className="border p-1 rounded text-sm"
                          value={appointment.status}
                          onChange={(e) => handleStatusChange(appointment.id, e.target.value)}
                        >
                          <option value="pending">Pendiente</option>
                          <option value="confirmed">Confirmada</option>
                          <option value="completed">Completada</option>
                          <option value="cancelled">Cancelada</option>
                        </select>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="p-8 text-center text-gray-500">
                      <Calendar className="w-16 h-16 mx-auto mb-3 text-gray-300" />
                      <p>No hay citas programadas</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}